import cx from "classnames"
import {isEqual} from "lodash"
import {DragDropContext} from "react-beautiful-dnd"
import {academicTrackPhasesAndShiftsTemplatesService} from "services"
import {BaseLoading} from "components/Loading"
import {DraggableResult} from "types/common"
import {toastError} from "helpers"
import styles from "./AcademicTrackPhasesAndShifts.module.css"
import {useVisible} from "hooks"
import {useMutation, useQuery} from "@tanstack/react-query"
import {AcademicPlans} from "types/academicPlans"
import SelectPhasePopup from "../SelectPhasePopup"
import {PhaseItem} from "../AcademicTrack/PhaseItem/PhaseItem"

type Props = {
    academicTrack: any
}

export function AcademicTrackPhasesAndShifts(props: Props) {
    const {academicTrack} = props
    const popupSelectPhase = useVisible()

    const getPhasesAndShiftsQuery = useQuery(
        ["get-academic-track-phases-and-shifts"],
        () => {
            return academicTrackPhasesAndShiftsTemplatesService.getAcademicTrackPhaseTemplates({
                academicTrackId: academicTrack.trackId
            })
        },
        {
            enabled: !!academicTrack,
            initialData: []
        }
    )

    const editShiftMutation = useMutation(academicTrackPhasesAndShiftsTemplatesService.editShiftTemplate, {
        onSuccess: () => {
            getPhasesAndShiftsQuery.refetch()
        },
        onError: () => {
            toastError("Failed to update shift")
        }
    })

    const onDragEnd = async (result: DraggableResult) => {
        const {destination, source, draggableId} = result || {}
        if (isEqual(destination, source) || !destination) {
            return
        }
        const [destProgramVersionPhaseShiftTemplateId, destProgramVersionPhaseShiftTemplateShiftId] =
            destination.droppableId.split("_")
        const [sourceProgramVersionPhaseShiftTemplateId, sourceProgramVersionPhaseShiftTemplateShiftId] =
            source.droppableId.split("_")

        if (destProgramVersionPhaseShiftTemplateId === AcademicPlans.PhaseDraggable.PhaseSectionEmpty) {
            const [_, academicTrackPhaseShiftTemplateShiftId] = draggableId.split("_")
            return moveCourse({
                academicTrackPhaseShiftTemplateShiftId: Number(academicTrackPhaseShiftTemplateShiftId),
                academicTrackPhaseShiftTemplateId: Number(destProgramVersionPhaseShiftTemplateShiftId),
                sourceAcademicTrackPhaseShiftTemplateId: Number(sourceProgramVersionPhaseShiftTemplateId),
                position: 0
            })
        }

        const [_, academicTrackPhaseShiftTemplateShiftId] = draggableId.split("_")
        if (destProgramVersionPhaseShiftTemplateId !== sourceProgramVersionPhaseShiftTemplateId) {
            moveCourse({
                academicTrackPhaseShiftTemplateShiftId: Number(academicTrackPhaseShiftTemplateShiftId),
                academicTrackPhaseShiftTemplateId: Number(destProgramVersionPhaseShiftTemplateId),
                sourceAcademicTrackPhaseShiftTemplateId: Number(sourceProgramVersionPhaseShiftTemplateId),
                position: destination.index
            })
        } else {
            moveCourse({
                academicTrackPhaseShiftTemplateShiftId: Number(academicTrackPhaseShiftTemplateShiftId),
                academicTrackPhaseShiftTemplateId: Number(destProgramVersionPhaseShiftTemplateId),
                sourceAcademicTrackPhaseShiftTemplateId: Number(sourceProgramVersionPhaseShiftTemplateId),
                position: destination.index
            })
        }
    }

    const moveCourse = async (data: {
        academicTrackPhaseShiftTemplateShiftId: number
        academicTrackPhaseShiftTemplateId: number
        sourceAcademicTrackPhaseShiftTemplateId: number
        position: number
    }) => {
        let shifts =
            getPhasesAndShiftsQuery.data.find(
                (item) => item.academicTrackPhaseShiftTemplateId === data.sourceAcademicTrackPhaseShiftTemplateId
            )?.shifts ?? []

        if (data.academicTrackPhaseShiftTemplateId === data.sourceAcademicTrackPhaseShiftTemplateId) {
            shifts = shifts.map((shift) => {
                if (
                    shift.position >= data.position &&
                    shift.academicTrackPhaseShiftTemplateShiftId !== data.academicTrackPhaseShiftTemplateShiftId
                ) {
                    shift.position = shift.position + 1
                } else if (
                    shift.academicTrackPhaseShiftTemplateShiftId === data.academicTrackPhaseShiftTemplateShiftId
                ) {
                    shift.position = data.position
                }

                return {
                    position: shift.position,
                    academicTrackPhaseShiftTemplateShiftId: shift.academicTrackPhaseShiftTemplateShiftId,
                    academicTrackPhaseShiftTemplateId: shift.academicTrackPhaseShiftTemplateId
                }
            })

            return editShiftMutation.mutate({
                shiftTemplates: shifts
            })
        }

        editShiftMutation.mutate({
            shiftTemplates: [
                {
                    academicTrackPhaseShiftTemplateShiftId: data.academicTrackPhaseShiftTemplateShiftId,
                    academicTrackPhaseShiftTemplateId: data.academicTrackPhaseShiftTemplateId,
                    position: data.position
                }
            ]
        })
    }

    const onAddNewPhase = async () => {
        popupSelectPhase.open()
    }

    return (
        <div className={cx("custom-scroll", styles.termBodyWrap)}>
            <BaseLoading isShow={getPhasesAndShiftsQuery.isLoading} />
            <DragDropContext onDragEnd={onDragEnd} className={styles.termItemWrap}>
                {getPhasesAndShiftsQuery.data.map((phaseItem, index) => (
                    <PhaseItem
                        key={index}
                        phaseItem={phaseItem}
                        academicTrack={academicTrack}
                        reloadView={getPhasesAndShiftsQuery.refetch}
                    />
                ))}
            </DragDropContext>
            <div className={styles.termItem}>
                <div className={styles.termBody} onClick={onAddNewPhase}>
                    Add new Phase
                </div>
            </div>

            <SelectPhasePopup
                isVisible={popupSelectPhase.isVisible}
                onClose={popupSelectPhase.close}
                academicTrackId={academicTrack.trackId}
                onRefresh={getPhasesAndShiftsQuery.refetch}
            />
        </div>
    )
}
